import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { voidESignature } from "@/features/esignatures/api";
import type { ESignatureRequest } from "@/features/esignatures/types";
import { ApiError } from "@/lib/api";

interface Props {
  request: ESignatureRequest | null;
  onOpenChange: (open: boolean) => void;
  onRevoked?: () => void;
}

/** Confirm-and-revoke the request (legacy sign_revoke). A joint (2-recipient)
 * request warns that revoking one recipient revokes both. */
export function RevokeDialog({ request, onOpenChange, onRevoked }: Props) {
  const [busy, setBusy] = useState(false);
  const isJoint = (request?.recipients.length ?? 0) >= 2;

  const handleRevoke = async () => {
    if (!request) return;
    setBusy(true);
    try {
      await voidESignature(request.id);
      toast.success("Signature request revoked.");
      onRevoked?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't revoke.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={!!request} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revoke this signature request?</AlertDialogTitle>
          <AlertDialogDescription>
            The signing link stops working immediately and the request is marked
            revoked.
            {isJoint && (
              <span className="text-warning mt-2 flex items-center gap-1.5 font-medium">
                <AlertTriangle className="size-4" /> This document has two
                recipients — revoking one revokes both.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={handleRevoke} disabled={busy}>
            {busy ? "Revoking…" : "Revoke"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
